"use client";

/**
 * TanStack Query hook for the explain endpoint (mission drilldown-substrate, WP05/T017).
 *
 * Keyed `[run_id, address..., page, page_size, step_key]` (research R5) — cache-friendly
 * paging/step-filtering: switching pages or a step filter for the SAME figure re-fetches only
 * that page (a prior page's cache entry stays warm), and switching to a DIFFERENT figure never
 * shows stale contributing rows under the new header (a fresh query key entirely). `enabled` is
 * false until both a run and an address are known, so the panel never fires a request before a
 * figure has actually been clicked.
 *
 * Surfaces a typed error via TanStack Query's own `isError`/`error` — `ExplainPanel` renders an
 * honest failure message from it rather than spinning forever (T017's own requirement).
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { fetchExplain } from "./explainClient";
import type { CellAddress, ExplainResult } from "./explainTypes";

export interface UseExplainParams {
  runId: string | null;
  address: CellAddress | null;
  page?: number;
  pageSize?: number;
  stepKey?: string | null;
}

/** `year`/`scenario` ride along in the query key verbatim (including `0`/`null`) — see
 * `explainClient.ts`'s module docstring on why year=0 is never treated as absent. */
function addressKeyParts(
  address: CellAddress | null,
): (string | number | null)[] {
  if (!address) return [null, null, null, null, null];
  return [
    address.component,
    address.key,
    address.metric,
    address.year,
    address.scenario,
  ];
}

export function useExplain({
  runId,
  address,
  page = 1,
  pageSize = 50,
  stepKey = null,
}: UseExplainParams): UseQueryResult<ExplainResult, Error> {
  return useQuery({
    queryKey: [
      "explain",
      runId,
      ...addressKeyParts(address),
      page,
      pageSize,
      stepKey,
    ],
    queryFn: () =>
      fetchExplain({
        // Non-null: guarded by `enabled` below — the query never runs while either is null.
        runId: runId as string,
        address: address as CellAddress,
        page,
        pageSize,
        stepKey: stepKey ?? undefined,
      }),
    enabled: runId !== null && address !== null,
    retry: 1,
    staleTime: 30_000,
  });
}
